import {Controller} from "@hotwired/stimulus";
import {disabling} from "./mixins/disabling";
import Trix from "trix";

/**
 * Mentions controller allows to add mentions to Trix editor. It works by
 *
 * - observing keyup events in the editor
 * - when a "word" starts with an `@`, it requests data from the server which it
 *   uses to render a drop down with the options
 * - the first option is automatically highlighted, ↑/↓ + Enter or mouse click
 *   replace the string by an ActionText attachment
 * - updating the "word" sends a new request
 *
 * The controller cannot be associated with the Trix editor itself because
 * rendering the drop down within it causes chaos.
 *
 * "Word" is sent to the BE as query param `q`. "Word" cannot contain spaces.
 */
export default class extends Controller {
  static targets = ["field", "dropDown", "option"];
  static values = {
    url: String
  };

  currentClass = 'current-self';

  connect() {
    super.connect();
    disabling(this);
    this.keyUpHandler = this._handleInput.bind(this);
    this.fieldTarget.addEventListener("keyup", this.keyUpHandler);
    this.clickOutsideDropDownHandler = this._clickOutsideDropDownHandler.bind(this);
  }

  disconnect() {
    super.disconnect();
    this.fieldTarget.removeEventListener("keyup", this.keyUpHandler);
  }

  _handleInput(event) {
    switch (event.key) {
      case 'ArrowDown':
        this._moveSelectionDown();
        break;
      case 'ArrowUp':
        this._moveSelectionUp();
        break;
      case 'Enter':
        this._insertSelected();
        break;
      default:
        this._regularInput();
    }
  }

  _moveSelectionDown() {
    if (!this.hasOptionTarget) {
      return;
    }

    let currentIndex = this.optionTargets.findIndex(option => option.classList.contains(this.currentClass));
    currentIndex = currentIndex === -1 ? -1 : currentIndex;

    const nextIndex = (currentIndex + 1) % this.optionTargets.length;

    if (currentIndex !== -1) {
      this.optionTargets[currentIndex].classList.remove(this.currentClass);
    }

    this.optionTargets[nextIndex].classList.add(this.currentClass);
  }

  _moveSelectionUp() {
    if (!this.hasOptionTarget) {
      return;
    }

    let currentIndex = this.optionTargets.findIndex(option => option.classList.contains(this.currentClass));
    currentIndex = currentIndex === -1 ? -1 : currentIndex;

    const prevIndex = (currentIndex === 0) ? this.optionTargets.length : currentIndex - 1;

    if (currentIndex !== -1) {
      this.optionTargets[currentIndex].classList.remove(this.currentClass);
    }

    this.optionTargets[prevIndex].classList.add(this.currentClass);
  }

  _insertSelected() {
    if (!this.hasOptionTarget) {
      return;
    }

    let currentIndex = this.optionTargets.findIndex(option => option.classList.contains(this.currentClass));
    if (currentIndex === -1) {
      return;
    }

    this.fieldTarget.editor.deleteInDirection("backward") // delete the new line
    this.optionTargets[currentIndex].dispatchEvent(new CustomEvent('click'));
  }

  _regularInput() {
    const lastWord = this._currentlyEditedWord();
    if (lastWord[0] === '@') {
      this._showMentions(lastWord);
    } else {
      this._hideMentions();
    }
  }

  _currentlyEditedWord() {
    const trix = this.fieldTarget.editor;
    const textBeforeCaret = trix.getDocument().toString().slice(0, this._caretPosition(trix));
    const linesBeforeCaret = textBeforeCaret.split('\n');
    const wordsBeforeCaret = linesBeforeCaret[linesBeforeCaret.length - 1].split(' ')
    return wordsBeforeCaret[wordsBeforeCaret.length - 1];
  }

  _caretPosition(e) {
    return e.getSelectedRange()[0];
  }

  insertMention(e) {
    let attachment = new Trix.Attachment({
      sgid: e.params.sgid,
      content: e.params.content,
    });

    const trix = this.fieldTarget.editor;
    const wordToReplace = this._currentlyEditedWord();
    const caretPosition = this._caretPosition(trix);
    trix.setSelectedRange([caretPosition - wordToReplace.length, caretPosition]);
    trix.insertAttachment(attachment);
    trix.insertString(" ");
    this._hideMentions();
  }

  _showMentions(word) {
    const url = new URL(this.urlValue, window.location);
    url.searchParams.set('q', word.slice(1, word.length));

    fetch(url.toString())
      .then(response => response.json())
      .then(this._updateDropDown)
      .catch(error => {
        console.error("Error fetching mentions:", error);
        this._updateDropDown([]);
      });

    const dropDown = this._dropDown();
    this._positionDropDown(dropDown, word);
    if (dropDown.children.length > 0) this.enable(dropDown);

    document.addEventListener('click', this.clickOutsideDropDownHandler);
  }

  _hideMentions() {
    if (this.hasDropDownTarget) {
      this.disable(this.dropDownTarget);
      document.removeEventListener('click', this.clickOutsideDropDownHandler);
    }
  }

  _updateDropDown = (mentions) => {
    if (!this.hasDropDownTarget) {
      return; // almost impossible timing
    }
    this.dropDownTarget.innerHTML = '';

    if (mentions.length === 0) {
      this._noMatches();
    } else {
      this._updateMatches(mentions);
    }
    this.enable(this.dropDownTarget);
  }

  _noMatches() {
    const noMatches = document.createElement('li');
    noMatches.classList.add('block', 'py-1', 'px-2', 'w-full', 'text-grey-700', 'bg-grey-200');
    noMatches.innerText = window.I18n.t('legal_person.no_matches');
    this.dropDownTarget.appendChild(noMatches)
  }

  _updateMatches(mentions) {
    let i = 0;
    mentions.forEach(mention => {
      const node = document.createElement('li');
      node.classList.add('block', 'py-1', 'px-2', 'w-full', 'text-grey-700');
      node.classList.add('cursor-pointer', 'hover:text-black', 'hover:bg-blue-100');
      node.classList.add('current-self:text-black', 'current-self:bg-blue-100');
      if (i === 0) {
        node.classList.add(this.currentClass);
      }
      node.innerText = mention.name;
      node.dataset.mentionsSgidParam = mention.attachable_sgid;
      node.dataset.mentionsContentParam = mention.content;
      node.dataset.action = 'click->mentions#insertMention';
      node.dataset.mentionsTarget = 'option';

      this.dropDownTarget.appendChild(node);

      i++;
    })
  }

  _dropDown() {
    if (!this.hasDropDownTarget) {
      return this._createDropDown();
    }
    return this.dropDownTarget;
  }

  _createDropDown() {
    const dropDown = document.createElement("ul");
    dropDown.hidden = true;
    dropDown.classList.add('absolute', 'z-10', 'mt-px', 'min-w-32');
    dropDown.classList.add('flex', 'flex-col', 'divide-y', 'divide-grey-200');
    dropDown.classList.add('bg-white', 'border', 'border-grey-300', 'rounded', 'shadow', 'overflow-hidden');
    dropDown.dataset.mentionsTarget = "dropDown";
    this.element.appendChild(dropDown);
    return dropDown;
  }

  _positionDropDown(dropDown, word) {
    const sel = window.getSelection();
    const caretPosition = sel.getRangeAt(0).startOffset;

    const range = document.createRange();
    range.setStart(sel.anchorNode, caretPosition - word.length);
    range.setEnd(sel.anchorNode, caretPosition);
    range.collapse(true);

    const rect = range.getBoundingClientRect();

    dropDown.style.top = `${rect.top + rect.height + window.scrollY}px`;
    dropDown.style.left = `${rect.left + window.scrollX}px`;
  }

  _clickOutsideDropDownHandler(event) {
    if (event.target.isConnected && !this.element.contains(event.target)) {
      this._hideMentions();
    }
  }
}